/**
 * Linha do tempo do ambiente e do setor: os marcos que a planilha registra
 * (datas das etapas) intercalados com o que os gestores apontaram no app
 * (eventos do backend), em ordem cronológica. As tarefas e as PEÇAS
 * DESMEMBRADAS do ambiente entram na mesma linha.
 *
 * Marco da planilha só tem DATA (sem hora): entra à meia-noite local do dia
 * e vem marcado `diaInteiro`, para a tela não inventar horário.
 *
 * Instante do app (ISO com fuso) é lido com `dataLocalDe`, nunca com
 * `toDate`: `toDate` corta "2026-09-22T14:03:00-03:00" no dia e perde a hora.
 */
import { buildAmbienteEtapas } from "../../../utils/ambienteTimeline";
import { dataLocalDe } from "../../../utils/dinheiro";
import { startOfDay, toDate } from "../../../utils/status";
import { chaveAmbiente } from "./chaves";

export const ORIGEM = Object.freeze({ PLANILHA: "planilha", APP: "app" });

// titulo: o que aparece em negrito; categoria: ícone; tom: cor (só estado).
const TIPOS_DE_EVENTO = {
  AMBIENTE_ALOCADO: { titulo: "Ambiente distribuído", categoria: "alocacao", tom: "neutral" },
  AMBIENTE_INICIADO: { titulo: "Produção iniciada", categoria: "status", tom: "info" },
  AMBIENTE_RELIGADO: { titulo: "Ambiente religado (renomeado na planilha)", categoria: "status", tom: "neutral" },
  AMBIENTE_PAUSADO: { titulo: "Produção pausada", categoria: "status", tom: "warning" },
  AMBIENTE_RETOMADO: { titulo: "Produção retomada", categoria: "status", tom: "info" },
  AMBIENTE_AGUARDANDO: { titulo: "Voltou para aguardando", categoria: "status", tom: "neutral" },
  AMBIENTE_PRAZO_ALTERADO: { titulo: "Prazo do ambiente alterado", categoria: "prazo", tom: "warning" },
  AMBIENTE_COMENTARIO: { titulo: "Comentário", categoria: "comentario", tom: "neutral" },
  AMBIENTE_PROJETISTA: { titulo: "Projetista do executivo", categoria: "alocacao", tom: "neutral" },
  AMBIENTE_EXCECAO_CARGA: { titulo: "Carga na marcenaria marcada", categoria: "status", tom: "neutral" },
  TAREFA_CRIADA: { titulo: "Tarefa criada", categoria: "tarefa", tom: "neutral" },
  TAREFA_EDITADA: { titulo: "Tarefa editada", categoria: "tarefa", tom: "neutral" },
  TAREFA_ATRIBUIDA: { titulo: "Tarefa atribuída", categoria: "tarefa", tom: "neutral" },
  TAREFA_INICIADA: { titulo: "Tarefa iniciada", categoria: "status", tom: "info" },
  TAREFA_PAUSADA: { titulo: "Tarefa pausada", categoria: "status", tom: "warning" },
  TAREFA_RETOMADA: { titulo: "Tarefa retomada", categoria: "status", tom: "info" },
  TAREFA_CONCLUIDA: { titulo: "Tarefa concluída", categoria: "status", tom: "success" },
  TAREFA_CANCELADA: { titulo: "Tarefa cancelada", categoria: "status", tom: "neutral" },
  TAREFA_REABERTA: { titulo: "Tarefa reaberta", categoria: "status", tom: "neutral" },
  TAREFA_PRAZO_ALTERADO: { titulo: "Prazo da tarefa alterado", categoria: "prazo", tom: "warning" },
  TAREFA_COMENTARIO: { titulo: "Comentário na tarefa", categoria: "comentario", tom: "neutral" },
  PECA_CRIADA: { titulo: "Peça desmembrada", categoria: "peca", tom: "neutral" },
  PECA_ALTERADA: { titulo: "Peça editada", categoria: "peca", tom: "neutral" },
  PECA_ALOCADA: { titulo: "Peça movida", categoria: "alocacao", tom: "neutral" },
  PECA_INICIADA: { titulo: "Peça iniciada", categoria: "status", tom: "info" },
  PECA_PAUSADA: { titulo: "Peça pausada", categoria: "status", tom: "warning" },
  PECA_RETOMADA: { titulo: "Peça retomada", categoria: "status", tom: "info" },
  PECA_AGUARDANDO: { titulo: "Peça voltou para aguardando", categoria: "status", tom: "neutral" },
  PECA_RETORNADA: { titulo: "Peça voltou para o ambiente", categoria: "status", tom: "success" },
  PECA_CONCLUIDA: { titulo: "Peça concluída sem retorno", categoria: "status", tom: "success" },
  PECA_CANCELADA: { titulo: "Desmembramento cancelado", categoria: "status", tom: "neutral" },
  PECA_REABERTA: { titulo: "Peça reaberta", categoria: "status", tom: "neutral" },
  PECA_PRAZO_ALTERADO: { titulo: "Prazo da peça alterado", categoria: "prazo", tom: "warning" },
  PECA_COMENTARIO: { titulo: "Comentário na peça", categoria: "comentario", tom: "neutral" },
  SETOR_CRIADO: { titulo: "Setor criado", categoria: "cadastro", tom: "neutral" },
  SETOR_ALTERADO: { titulo: "Setor alterado", categoria: "cadastro", tom: "neutral" },
  SETOR_FORMA_ALTERADA: { titulo: "Desenho do mapa alterado", categoria: "cadastro", tom: "neutral" },
  SETOR_REMOVIDO: { titulo: "Setor excluído", categoria: "cadastro", tom: "neutral" },
  SUBSETOR_CRIADO: { titulo: "Subsetor criado", categoria: "cadastro", tom: "neutral" },
  SUBSETOR_ALTERADO: { titulo: "Subsetor alterado", categoria: "cadastro", tom: "neutral" },
  SUBSETOR_REMOVIDO: { titulo: "Subsetor excluído", categoria: "cadastro", tom: "neutral" },
  POSTO_CRIADO: { titulo: "Posto criado", categoria: "cadastro", tom: "neutral" },
  POSTO_ALTERADO: { titulo: "Posto alterado", categoria: "cadastro", tom: "neutral" },
  POSTO_REMOVIDO: { titulo: "Posto excluído", categoria: "cadastro", tom: "neutral" },
  COLABORADOR_CRIADO: { titulo: "Colaborador cadastrado", categoria: "cadastro", tom: "neutral" },
  COLABORADOR_ALTERADO: { titulo: "Colaborador alterado", categoria: "cadastro", tom: "neutral" },
  COLABORADOR_REMOVIDO: { titulo: "Colaborador excluído", categoria: "cadastro", tom: "neutral" },
};

const TIPO_DESCONHECIDO = { titulo: "Atualização", categoria: "status", tom: "neutral" };

/** {titulo, categoria, tom} de um tipo de evento do backend. */
export function descreverTipoDeEvento(tipo) {
  return TIPOS_DE_EVENTO[tipo] || TIPO_DESCONHECIDO;
}

// Mesmos nomes do backend (`mapa_producao/excecoes.MOTIVOS`).
const MOTIVO_DA_CARGA = Object.freeze({
  curva: "curva",
  item_especial: "item especial",
  volume_grande: "volume grande",
  outro: "outro motivo",
});

/**
 * Título que depende dos dados do evento. A marca de carga diz a classe no
 * próprio título ("Marcado como pesado · curva"): na linha do tempo é a
 * informação inteira, e o texto do backend repete o nome do ambiente.
 */
function tituloDoEvento(evento, descricao) {
  if (evento.tipo !== "AMBIENTE_EXCECAO_CARGA") return descricao.titulo;
  const { classe, motivo } = evento.dados || {};
  if (!classe) return descricao.titulo;
  const complemento = MOTIVO_DA_CARGA[motivo] ? ` · ${MOTIVO_DA_CARGA[motivo]}` : "";
  return `Marcado como ${classe}${complemento}`;
}

// A planilha guarda "YYYY-MM-DD" (ou Date); texto como "X" ou "." em coluna
// de data é marcação, não data — e `new Date("1")` seria o ano 2001.
const RE_DATA_PLANILHA = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/;

function dataDaPlanilha(valor) {
  if (valor instanceof Date) return toDate(valor);
  if (typeof valor !== "string" || !RE_DATA_PLANILHA.test(valor.trim())) return null;
  return toDate(valor.trim());
}

/** Marcos da linha da planilha que têm data, cada um como item da timeline. */
export function marcosDaPlanilha(row) {
  return buildAmbienteEtapas(row)
    .map((etapa) => ({ etapa, quando: dataDaPlanilha(etapa.data) }))
    .filter(({ quando }) => quando)
    .map(({ etapa, quando }) => ({
      id: `planilha:${etapa.key}`,
      quando: startOfDay(quando),
      diaInteiro: true,
      titulo: etapa.titulo,
      texto: "Data registrada na planilha",
      autor: null,
      tipo: `PLANILHA_${etapa.key.toUpperCase().replace(/-/g, "_")}`,
      origem: ORIGEM.PLANILHA,
      categoria: "planilha",
      tom: "neutral",
      chave: null,
      tarefaId: null,
      pecaId: null,
      dados: { campo: etapa.campo },
    }));
}

/** Um Evento do backend como item da timeline (`quando` = criado_em). */
export function itemDeEvento(evento) {
  const descricao = descreverTipoDeEvento(evento.tipo);
  return {
    id: `app:${evento.id}`,
    quando: dataLocalDe(evento.criado_em),
    diaInteiro: false,
    titulo: tituloDoEvento(evento, descricao),
    texto: evento.texto || "",
    autor: evento.usuario || null,
    tipo: evento.tipo,
    origem: ORIGEM.APP,
    categoria: descricao.categoria,
    tom: descricao.tom,
    chave: evento.chave || null,
    tarefaId: evento.tarefa_id || null,
    pecaId: evento.peca_id || null,
    dados: evento.dados || {},
  };
}

// Marcos que o resumo da tarefa já carrega; só entram se o backend não
// mandou o evento correspondente (a tela pode não ter buscado os eventos
// de cada tarefa).
const MARCOS_DA_TAREFA = [
  { tipo: "TAREFA_CRIADA", campo: "criado_em", autor: "criado_por" },
  { tipo: "TAREFA_INICIADA", campo: "iniciado_em", autor: null },
  { tipo: "TAREFA_CONCLUIDA", campo: "concluido_em", autor: null },
];

function marcosDasTarefas(tarefas, eventos) {
  const jaVieram = new Set(eventos.map((e) => `${e.tarefa_id}:${e.tipo}`));
  return (tarefas || []).flatMap((tarefa) =>
    MARCOS_DA_TAREFA.filter((m) => tarefa[m.campo] && !jaVieram.has(`${tarefa.id}:${m.tipo}`)).map((m) => {
      const descricao = descreverTipoDeEvento(m.tipo);
      return {
        id: `tarefa:${tarefa.id}:${m.tipo}`,
        quando: dataLocalDe(tarefa[m.campo]),
        diaInteiro: false,
        titulo: descricao.titulo,
        texto: [tarefa.codigo, tarefa.titulo].filter(Boolean).join(" · "),
        autor: (m.autor && tarefa[m.autor]) || null,
        tipo: m.tipo,
        origem: ORIGEM.APP,
        categoria: descricao.categoria,
        tom: descricao.tom,
        chave: tarefa.ambiente_chave || null,
        tarefaId: tarefa.id,
        pecaId: tarefa.peca_id || null,
        dados: {},
      };
    })
  );
}

// Marcos que o resumo da peça já carrega, no mesmo espírito dos da tarefa: só
// entram se o backend não mandou o evento correspondente.
const MARCOS_DA_PECA = [
  { tipo: "PECA_CRIADA", campo: "criado_em", autor: "criado_por" },
  { tipo: "PECA_RETORNADA", campo: "retornado_em", autor: null },
  { tipo: "PECA_CONCLUIDA", campo: "concluido_em", autor: null },
];

function marcosDasPecas(pecas, eventos) {
  const jaVieram = new Set(eventos.map((e) => `${e.peca_id}:${e.tipo}`));
  return (pecas || []).flatMap((peca) =>
    MARCOS_DA_PECA.filter((m) => peca[m.campo] && !jaVieram.has(`${peca.id}:${m.tipo}`)).map((m) => {
      const descricao = descreverTipoDeEvento(m.tipo);
      return {
        id: `peca:${peca.id}:${m.tipo}`,
        quando: dataLocalDe(peca[m.campo]),
        diaInteiro: false,
        titulo: descricao.titulo,
        texto: [peca.codigo, peca.nome].filter(Boolean).join(" · "),
        autor: (m.autor && peca[m.autor]) || null,
        tipo: m.tipo,
        origem: ORIGEM.APP,
        categoria: descricao.categoria,
        tom: descricao.tom,
        chave: peca.ambiente_chave || peca.ambienteChave || null,
        tarefaId: null,
        pecaId: peca.id,
        dados: {},
      };
    })
  );
}

const daChave = (itens, chave) =>
  (itens || []).filter((item) => chave && (item.ambiente_chave || item.ambienteChave) === chave);

function semRepetidos(eventos) {
  const vistos = new Set();
  return (eventos || []).filter((e) => {
    if (!e || vistos.has(e.id)) return false;
    vistos.add(e.id);
    return true;
  });
}

// Mesmo instante: planilha antes do app (o marco do dia abre o dia).
function cronologico(a, b) {
  return (
    a.quando - b.quando ||
    (a.origem === b.origem ? 0 : a.origem === ORIGEM.PLANILHA ? -1 : 1) ||
    String(a.id).localeCompare(String(b.id))
  );
}

function ordenar(itens) {
  return itens.filter((item) => item.quando).sort(cronologico);
}

/**
 * Timeline de UM ambiente: marcos da linha da planilha + eventos do app
 * (GET /ambientes/eventos, e se quiser também os de /tarefas/{id}/eventos)
 * + marcos das tarefas e das PEÇAS do ambiente que não vieram como evento.
 * `row` nulo: só app (as tarefas e peças passadas entram todas).
 * Itens: {id, quando: Date, diaInteiro, titulo, texto, autor, tipo, origem,
 *         categoria, tom, chave, tarefaId, pecaId, dados}. Mais antigo
 * primeiro.
 */
export function montarTimelineAmbiente(row, eventos = [], tarefas = [], pecas = []) {
  const unicos = semRepetidos(eventos);
  // Com a linha, só as tarefas e peças DESTE ambiente (a tela pode passar todas).
  const chave = row ? chaveAmbiente(row) : null;
  return ordenar([
    ...(row ? marcosDaPlanilha(row) : []),
    ...unicos.map(itemDeEvento),
    ...marcosDasTarefas(row ? daChave(tarefas, chave) : tarefas, unicos),
    ...marcosDasPecas(row ? daChave(pecas, chave) : pecas, unicos),
  ]);
}

// O nome do setor é digitado pelo gestor: não há como saber o gênero dele para
// escrever "Chegou à Furação". "Chegou ao setor X" serve para qualquer nome.
function tituloDaChegada(nome) {
  return nome ? `Chegou ao setor ${nome}` : "Chegou ao setor";
}

function chegadaAoSetor(demanda) {
  return {
    id: `planilha:${demanda.chaveLinha || demanda.chave}:chegada`,
    quando: startOfDay(demanda.desde),
    diaInteiro: true,
    titulo: tituloDaChegada(demanda.setorNome),
    texto: [demanda.rotulo, demanda.cliente].filter(Boolean).join(" · "),
    autor: null,
    tipo: "PLANILHA_CHEGADA_SETOR",
    origem: ORIGEM.PLANILHA,
    categoria: "planilha",
    tom: "neutral",
    chave: demanda.chave || null,
    tarefaId: null,
    pecaId: null,
    dados: { setorId: demanda.setorId || null, etapa: demanda.etapa || null },
  };
}

/**
 * Timeline do SETOR: eventos do setor (GET /eventos?setor=) + a chegada de
 * cada ambiente ao setor pela planilha (a data da etapa anterior — `desde`).
 * `demandas` = as do setor (combinadas). Mais antigo primeiro.
 */
export function montarTimelineSetor(eventos = [], demandas = []) {
  const chegadas = (demandas || []).filter((d) => d.desde).map(chegadaAoSetor);
  return ordenar([...semRepetidos(eventos).map(itemDeEvento), ...chegadas]);
}

const dois = (n) => String(n).padStart(2, "0");
const chaveDoDia = (data) => `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;

/**
 * Itens agrupados por dia local, como a Linha do tempo de Requisições:
 * [{chave: "YYYY-MM-DD", data: Date, hoje: boolean, itens}].
 * `decrescente` (padrão): dia mais recente primeiro e, dentro do dia, o
 * mais recente no topo.
 */
export function agruparPorDia(itens, { decrescente = true, hoje = new Date() } = {}) {
  const chaveDeHoje = chaveDoDia(hoje);
  const ordenados = ordenar([...(itens || [])]);
  if (decrescente) ordenados.reverse();
  const grupos = new Map();
  ordenados.forEach((item) => {
    const chave = chaveDoDia(item.quando);
    if (!grupos.has(chave)) {
      grupos.set(chave, { chave, data: startOfDay(item.quando), hoje: chave === chaveDeHoje, itens: [] });
    }
    grupos.get(chave).itens.push(item);
  });
  return [...grupos.values()];
}
